(function () {
  if (customElements.get('bnexo-everart-gallery')) return;

  class BnexoEverartGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bnexo-everart__gallery');
      if (!this.track) return;

      this.items = [...this.track.querySelectorAll('.bnexo-everart__gallery-item')];
      if (this.items.length < 2) return;

      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayMs = Number(this.dataset.autoplay) || 4500;
      this.currentIndex = 0;
      this.total = this.items.length;
      this.step = 0;
      this.gap = 16;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.scrollEndTimer = null;
      this.userPaused = false;
      this.isVisible = true;

      this.prevBtn?.addEventListener('click', () => this.navigate(-1, true));
      this.nextBtn?.addEventListener('click', () => this.navigate(1, true));

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('pointerdown', () => this.pause(true));
      this.track.addEventListener('pointerup', () => this.scheduleResume());
      this.track.addEventListener('touchstart', () => this.pause(true), { passive: true });
      this.track.addEventListener('touchend', () => this.scheduleResume(), { passive: true });

      this.addEventListener('mouseenter', () => this.pause(true));
      this.addEventListener('mouseleave', () => {
        if (!this.reducedMotion) this.scheduleResume();
      });

      this.addEventListener('focusin', () => this.pause(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget) && !this.reducedMotion) {
          this.scheduleResume();
        }
      });

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isVisible = entry.isIntersecting;
              if (this.isVisible && !this.userPaused && !this.reducedMotion) {
                this.startAutoplay();
              } else {
                this.stopAutoplay();
              }
            });
          },
          { threshold: 0.2 }
        );
        this.io.observe(this);
      }

      this.measure();
      if (!this.reducedMotion) this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollEndTimer);
      this.resizeObserver?.disconnect();
      this.io?.disconnect();
    }

    measure() {
      const first = this.items[0];
      if (!first) return;

      const style = getComputedStyle(this.track);
      this.gap = parseFloat(style.columnGap || style.gap) || 16;
      this.step = first.offsetWidth + this.gap;
      this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
    }

    getMaxIndex() {
      if (!this.step) return 0;
      const visible = Math.max(1, Math.floor(this.track.clientWidth / this.step));
      return Math.max(0, this.total - visible);
    }

    navigate(direction, manual) {
      if (manual) this.pause(true);

      const maxIndex = this.getMaxIndex();
      this.currentIndex += direction;

      if (this.currentIndex > maxIndex) this.currentIndex = 0;
      if (this.currentIndex < 0) this.currentIndex = maxIndex;

      this.scrollToIndex(this.currentIndex, true);
      if (manual) this.scheduleResume();
    }

    scrollToIndex(index, smooth) {
      this.track.scrollTo({
        left: index * this.step,
        behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto',
      });
    }

    tick() {
      const maxIndex = this.getMaxIndex();

      if (this.currentIndex >= maxIndex) {
        this.currentIndex = 0;
      } else {
        this.currentIndex += 1;
      }

      this.scrollToIndex(this.currentIndex, true);
    }

    onScroll() {
      if (!this.step) return;

      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(() => {
        const index = Math.round(this.track.scrollLeft / this.step);
        this.currentIndex = Math.min(Math.max(0, index), this.getMaxIndex());
      }, 80);
    }

    pause(fromUser) {
      if (fromUser) this.userPaused = true;
      this.stopAutoplay();
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.userPaused = false;
        if (this.isVisible && !this.reducedMotion) this.startAutoplay();
      }, 5000);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.reducedMotion || !this.isVisible || this.userPaused) return;

      this.autoplayTimer = window.setInterval(() => this.tick(), this.autoplayMs);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('bnexo-everart-gallery', BnexoEverartGallery);
})();
